import axios from "axios";
import { errorMessage } from "../../utils";
import axiosInstance from "./axios-instance-api";
import ApiError from "../../utils/ApiError";

async function Login(email, password, recaptchaToken, deviceFingerprint, headerParams = false) {
  try {
    let result = await axios.post(
      `${process.env.REACT_APP_DB_PORT}/user/login`, {
        email, password, recaptchaToken, deviceFingerprint
      }
    );

    return headerParams ? result : result.data;
  } catch (error) {
    // return errorMessage("Login", error);
    if (error.response) {
      const backendError = error.response.data;
      
      // ✅ Create Error object and add properties
      const apiError = new Error(backendError.message || 'Login failed');
      apiError.status = error.response.status;
      apiError.lockedUntil = backendError.lockedUntil;
      apiError.retryAfter = backendError.retryAfter;
      apiError.success = false;
      
      throw apiError;
    } else if (error.request) {
      const apiError = new Error('No response from server. Please check your connection.');
      apiError.status = 0;
      apiError.networkError = true;
      throw apiError;
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
}

async function VerifyMfaCode(
  email, userProvidedCode, mfaPendingCredential, sessionInfo, userId, deviceFingerprint, headerParams = false
) {
  try {
    let result = await axios.post(
      `${process.env.REACT_APP_DB_PORT}/user/mfa-code/verify`, {
        email, userProvidedCode, mfaPendingCredential, sessionInfo, userId, deviceFingerprint
      }
    );

    return headerParams ? result : result.data;
  } catch (error) {
    // return errorMessage("VerifyMfaCode", error);
    if (error.response) {
      // Backend returned an error response
      const backendError = error.response.data;
      
      throw new ApiError(
        backendError.message || 'MFA verification failed',
        error.response.status,
        {
          lockedUntil: backendError.lockedUntil,
          retryAfter: backendError.retryAfter
        }
      );
    } else if (error.request) {
      // Request sent but no response
      throw new ApiError(
        'No response from server. Please check your connection.',
        0,
        { networkError: true }
      );
    } else {
      // Request setup failed
      throw new ApiError(
        error.message || 'An unexpected error occurred',
        0
      );
    }
  }
}

async function ResendMfaCode(email, uid, mfaPendingCredential, mfaEnrollmentId, recaptchaToken, headerParams = false) {
  try {
    let result = await axios.post(
      `${process.env.REACT_APP_DB_PORT}/user/mfa-code/resend`, { email, uid, mfaPendingCredential, mfaEnrollmentId, recaptchaToken }
    );

    return headerParams ? result : result.data;
  } catch (error) {
    // return errorMessage("ResendMfaCode", error);
    if (error.response) {
      // Backend returned an error response
      const backendError = error.response.data;
      
      throw new ApiError(
        backendError.message || 'Failed to resend code',
        error.response.status,
        {
          retryAfter: backendError.retryAfter
        }
      );
    } else if (error.request) {
      // Request sent but no response
      throw new ApiError(
        'No response from server. Please check your connection.',
        0,
        { networkError: true }
      );
    } else {
      // Request setup failed
      throw new ApiError(
        error.message || 'An unexpected error occurred',
        0
      );
    }
  }
}

async function GetOwnUserInfo(uid, headerParams = false) {
  try {
    let result = await axiosInstance().post(`/user/user-data/get`, { uid });

    if (!result.data.success) {
      throw result.data.message;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    return errorMessage("GetOwnUserInfo", error);
  }
}

async function GetUserList(uid, headerParams = false) {
  try {
    let result = await axiosInstance().post(`/user/user-list/get`, { uid });

    if (!result.data.success) {
      throw result.data.message;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    return errorMessage("GetUserList", error);
  }
}

async function SelfRegistration(parameters, headerParams = false) {
  try {
    let result = await axios.post(
      `${process.env.REACT_APP_DB_PORT}/user/self-registration`, parameters
    );

    return headerParams ? result : result.data;
  } catch (error) {

    errorMessage("SelfRegistration", error);
		return error
  }
}

async function AddUser(parameters, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `/user/add`, parameters
    );

    if (!result.data.success) {
      throw result.data.message;
    }
    return headerParams ? result : result.data;
  } catch (error) {

    errorMessage("AddUser", error);
		return error
  }
}

async function EditUserInfo(userId, parameters, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `/user/edit/${userId}`,
      parameters  
    );
    
    if (!result.data.success) {
      throw result.data.message || result.data.error;
    }
    return headerParams ? result : result.data;
  } catch (error) {

    errorMessage("EditUserInfo", error);
		return error
  }
}

async function ForgotPassword(parameters, headerParams = false) {
  try {
    let result = await axios.post(
      `${process.env.REACT_APP_DB_PORT}/user/forgot-password`,
      parameters
    );
    
    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("ForgotPassword", error);
		return error
  }
}

async function EditPreferredClinic(userId, preferredClinicId, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/preferred-clinic/edit`, {
        uid: userId,
        userId,
        preferredClinicId
      }
    );
    
    return result
  } catch (error) {
    errorMessage("EditPreferredClinic", error);
		return error
  }
}

async function GetPreferredClinic(userId, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/preferred-clinic/get`, {
        uid: userId,
        userId
      }
    );
    
    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("GetPreferredClinic", error);
		return error
  }
}

async function VerifyEmail(firstName, email, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/email/verify`, { firstName, email });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("VerifyEmail", error);
		return error
  }
}

async function CheckPasswordValidity(email, password, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/password-validity/verify`, { email, password });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("CheckPasswordValidity", error);
		return error
  }
}

async function SendMfaCode(email, phoneNumber, recaptchaToken, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/mfa-code/send`, { email, phoneNumber, recaptchaToken });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("SendMfaCode", error);
		return error
  }
}

async function UpdateMfaPhoneNumber(sessionInfo, code, uid, newPhoneNumber, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/mfa-phone-number/update`, { 
        sessionInfo,
        code,
        uid,
        newPhoneNumber 
      });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("UpdateMfaPhoneNumber", error);
		return error
  }
}

async function AdminUpdateMfaPhoneNumber(uid, userId, newPhoneNumber, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/mfa-phone-number/admin-update`, { 
        uid,
        userId,
        newPhoneNumber 
      });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("AdminUpdateMfaPhoneNumber", error);
		return error
  }
}

async function AdminChangePassword(userId, parameters, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/change-password/admin`, {
        uid: userId,
        userId,
        ...parameters
      });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("AdminChangePassword", error);
		return error
  }
}

async function GetUserDevices(userId, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/devices`, {
        uid: userId,
        userId,
      });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("GetUserDevices", error);
		return error
  }
}

async function RemoveTrustedDevice(userId, deviceFingerprint, headerParams = false) {
  try {
    let result = await axiosInstance().post(
      `${process.env.REACT_APP_DB_PORT}/user/device/remove`, {
        uid: userId,
        userId,
        deviceFingerprint,
      });

    if (result.data) {
      throw result.data;
    }
    return headerParams ? result : result.data;
  } catch (error) {
    errorMessage("RemoveTrustedDevice", error);
		return error
  }
}

async function AddUserFeatureAccess(uid, feature) {
  try {
    let result = await axiosInstance().post(`/user/feature/add`, {
      uid,
      feature
    });

    if (result.status === 200 && result.data) {
      return result.data;
    } else {
      return false;
    }
  } catch (error) {
    return errorMessage("AddUserFeatureAccess", error);
  }
}

async function RevokeUserFeatureAccess(uid, feature) {
  try {
    let result = await axiosInstance().post(`/user/feature/revoke`, {
      uid,
      feature
    });

    if (result.status === 200 && result.data) {
      return result.data;
    } else {
      return false;
    }
  } catch (error) {
    return errorMessage("RevokeUserFeatureAccess", error);
  }
}

const Table = {
  Login,
  VerifyMfaCode,
  ResendMfaCode,
  GetOwnUserInfo,
  GetUserList,
  SelfRegistration,
  AddUser,
  EditUserInfo,
  ForgotPassword,
  EditPreferredClinic,
  GetPreferredClinic,
  VerifyEmail,
  CheckPasswordValidity,
  SendMfaCode,
  UpdateMfaPhoneNumber,
  AdminUpdateMfaPhoneNumber,
  AdminChangePassword,
  GetUserDevices,
  RemoveTrustedDevice,
  AddUserFeatureAccess,
  RevokeUserFeatureAccess
};

export default Table;
